import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { ApiResponse, Override, ReadModel } from '../../../../core/api/adapters';
import type { FilteredTableDetail } from '../../../../core/api/schema';
import type { ITableItem } from '../../../../project/models';
import type { ITableBackendResponse } from '../../../utils/table-back-to-front-convert.util';
import { convertTable } from '../../../utils/table-back-to-front-convert.util';
import type {
  IFilteredTable,
  IFilteredTableCreateParams,
  FilteredTableId,
  IFilteredTableUpdateParams,
  IWrittenFilteredTable,
} from '../models';

// `columns` is a read/write-hybrid FE union — a documented boundary until the write-side phase
// threads FilteredTableColumn/FilteredTableOperand.
type IFilteredTableBackendResponse = Override<
  ReadModel<FilteredTableDetail, FilteredTableId>,
  { table: ITableBackendResponse; columns: IFilteredTable['columns'] }
>;

@Injectable({
  providedIn: 'root',
})
export class FilteredTableApiService {
  private http = inject(HttpClient);

  createFilteredTable(body: IFilteredTableCreateParams): Observable<IWrittenFilteredTable> {
    return this.http.post<Override<ApiResponse<'filteredtable_create'>, Pick<IWrittenFilteredTable, 'columns'>>>(
      `/api/filteredtable/`,
      body,
    );
  }

  getFilteredTable(id: FilteredTableId, originServer = ''): Observable<IFilteredTable<ITableItem>> {
    return this.http.get<IFilteredTableBackendResponse>(`${originServer}/api/filteredtable/${id}`).pipe(
      map(t => ({
        ...t,
        table: convertTable(t.table),
      })),
    );
  }

  updateFilteredTable(id: FilteredTableId, params: IFilteredTableUpdateParams): Observable<IWrittenFilteredTable> {
    return this.http.put<Override<ApiResponse<'filteredtable_update'>, Pick<IWrittenFilteredTable, 'columns'>>>(
      `/api/filteredtable/${id}`,
      params,
    );
  }

  deleteFilteredTable(id: FilteredTableId): Observable<null> {
    return this.http.delete<null>(`/api/filteredtable/${id}`);
  }
}
